/**
 * Cross Team Sanity Service
 * Handles cross-team sanity request database operations
 */

import { BaseService } from './BaseService.js'
import { COLLECTIONS } from '../config.js'

export class CrossTeamSanityService extends BaseService {
  constructor() {
    super(COLLECTIONS.CROSS_TEAM_SANITY)
  }

  /**
   * Get the next available cross-team sanity ID(s) from the backend.
   * Uses the optimized /next-ids/:count endpoint which finds the max numeric
   * suffix with a single indexed sort instead of loading every record.
   * Falls back to a client-side scan when the endpoint is unavailable
   * (e.g. legacy SAN-format data, mock adapter, network failure).
   * @param {number} count - Number of IDs to generate (1..100)
   * @returns {Promise<string[]>} Array of cross_team_testing_NNNNN IDs
   */
  async getNextSanityIds(count = 1) {
    try {
      const { http } = await import('@/utils/httpClient')
      const ids = await http.get(`/crossteamsanities/next-ids/${count}`)
      if (Array.isArray(ids) && ids.length > 0) {
        return ids
      }
      return ['cross_team_testing_00001']
    } catch (error) {
      return ['cross_team_testing_00001']
    }
  }



  /**
   * Get the next available sanity ID
   * @returns {Promise<string>} Next sanity ID (e.g., cross_team_testing_00001)
   */
  async getNextSanityId() {
    try {
      const [id] = await this.getNextSanityIds(1)
      return id
    } catch (error) {
      // Fallback to timestamp-based ID if there's an error
      return `cross_team_testing_${Date.now().toString().slice(-5)}`
    }
  }

  /**
   * Create a new cross-team sanity request
   * @param {Object} sanityData - Sanity request data
   * @returns {Promise<Object>} Created sanity request
   */
  async createSanityRequest(sanityData) {
    try {
      
      // Generate sanity_id (e.g., cross_team_testing_00001)
      const sanity_id = await this.getNextSanityId()
      
      // Generate unique ID with cross_team_testing prefix
      const id = sanity_id

      // Prepare sanity request data
      const newSanityRequest = {
        ...sanityData,
        id,
        sanity_id,
        status: sanityData.status || 'active',
        teams: sanityData.teams || [],
        activities: sanityData.activities || [],
        is_deleted: false,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      }
      
      // Save to database
      const created = await this.create(newSanityRequest)
      return created
    } catch (error) {
      console.error('Error creating sanity request:', error)
      throw error
    }
  }

  /**
   * Update a sanity request
   * @param {string} id - Sanity request ID
   * @param {Object} updateData - Data to update
   * @returns {Promise<Object>} Updated sanity request
   */
  async updateSanityRequest(id, updateData) {
    return await this.update(id, {
      ...updateData,
      updated_at: new Date().toISOString()
    })
  }

  /**
   * Add a team to a sanity request
   * @param {string} sanityId - Sanity request ID
   * @param {Object} teamData - Team data (teamId, teamName, assignedMembers, status)
   * @returns {Promise<Object>} Updated sanity request
   */
  async addTeam(sanityId, teamData) {
    const sanityRequest = await this.getById(sanityId)
    if (!sanityRequest) {
      throw new Error('Sanity request not found')
    }

    const teams = sanityRequest.teams || []
    teams.push({
      ...teamData,
      status: teamData.status || 'yet_to_start'
    })

    return await this.update(sanityId, { 
      teams,
      updated_at: new Date().toISOString()
    })
  }

  /**
   * Update a team in a sanity request
   * @param {string} sanityId - Sanity request ID
   * @param {string} teamId - Team ID
   * @param {Object} teamData - Updated team data
   * @returns {Promise<Object>} Updated sanity request
   */
  async updateTeam(sanityId, teamId, teamData) {
    const sanityRequest = await this.getById(sanityId)
    if (!sanityRequest) {
      throw new Error('Sanity request not found')
    }

    const teams = sanityRequest.teams || []
    const teamIndex = teams.findIndex(t => t.teamId === teamId)
    
    if (teamIndex === -1) {
      throw new Error(`Team ${teamId} not found in sanity request`)
    }

    teams[teamIndex] = {
      ...teams[teamIndex],
      ...teamData
    }

    return await this.update(sanityId, { 
      teams,
      updated_at: new Date().toISOString()
    })
  }

  /**
   * Remove a team from a sanity request
   * @param {string} sanityId - Sanity request ID
   * @param {string} teamId - Team ID
   * @returns {Promise<Object>} Updated sanity request
   */
  async removeTeam(sanityId, teamId) {
    const sanityRequest = await this.getById(sanityId)
    if (!sanityRequest) {
      throw new Error('Sanity request not found')
    }

    const teams = (sanityRequest.teams || []).filter(t => t.teamId !== teamId)

    return await this.update(sanityId, { 
      teams,
      updated_at: new Date().toISOString()
    })
  }

  /**
   * Get active sanity requests
   * @returns {Promise<Array>} Active sanity requests
   */
  async getActiveSanityRequests() {
    const allRequests = await this.getAll()
    return allRequests.filter(request => 
      request.status === 'active' && !request.is_deleted
    )
  }

  /**
   * Soft delete a sanity request
   * @param {string} id - Sanity request ID
   * @param {string} deletedBy - User ID who deleted
   * @returns {Promise<Object>} Updated sanity request
   */
  async softDelete(id, deletedBy) {
    return await this.update(id, {
      is_deleted: true,
      deleted_at: new Date().toISOString(),
      deleted_by: deletedBy,
      updated_at: new Date().toISOString()
    })
  }

  /**
   * Restore a soft-deleted sanity request.
   * Uses the dedicated restore endpoint since regular update
   * filters out deleted records.
   * @param {string} id - Sanity request ID
   * @returns {Promise<Object>} Restored sanity request
   */
  async restore(id) {
    await this._ensureDatabaseService()
    const { buildApiHeaders, resolveInternalApiUrl } = await import('@/utils/apiHeaders')
    let baseUrl = (import.meta.env.VITE_API_BASE_URL ?? 'https://staging.services.leadconnectorhq.com/hltestmanager')
    if (this.db && this.db.adapter && typeof this.db.adapter.baseUrl === 'string') {
      baseUrl = this.db.adapter.baseUrl
    }
    const url = resolveInternalApiUrl(`/${this.collectionName}/${id}/restore`, baseUrl)
    const response = await fetch(url, {
      method: 'PATCH',
      headers: buildApiHeaders({ url, includeAuth: true }),
    })
    if (!response.ok) {
      throw new Error(`Failed to restore sanity request: ${response.statusText}`)
    }
    return await response.json()
  }
}

